<script lang="ts">
    import * as Popover from "$lib/components/ui/popover";
    import { daysOfWeekStrings, type CalendarDefaults, type daysOfWeek, type ShortWeekDayProperties } from "@/Common/constants";
    import { getDayMedians, getDisplayMedians, getUsualAvailability, uniqueId } from "@/Util/utils";
    import AvailabilityPopover from "./AvailabilityPopover.svelte";
    import { cn } from "@/lib/utils";

    /**
     * The week's default availability in one row, a day to a column, small enough for a phone: each day's riding time (or
     * Off) and intensity, and a day opens its availability (AvailabilityPopover) to change it.
     */
    export let defaults: CalendarDefaults
    export let trainingMedians: any
    export let disabled = false
    let className = ""
    export {className as class}

    const id = uniqueId("weekAvailability-")

    // Minutes of riding on a day: as set, or the day's usual from the medians.
    function minutes(dow: daysOfWeek, dow3Letter: ShortWeekDayProperties) {
        const duration = defaults[dow3Letter].duration
        return duration > 0 ? duration : Math.round(getDisplayMedians(getDayMedians(trainingMedians, dow)).duration*60)
    }
    // 45m, 1h, 1h05, 1h30: to 5 minutes, as the usual from the medians can be any minute
    function compact(m: number) {
        const rounded = Math.max(5, Math.round(m/5)*5)
        const hours = Math.floor(rounded/60), rest = rounded % 60
        return hours ? `${hours}h${rest ? String(rest).padStart(2, "0") : ""}` : `${rest}m`
    }
</script>

<div class={cn("grid grid-cols-7 gap-1 sm:gap-1.5", className)} role="group" aria-label="Weekly availability">
    {#each daysOfWeekStrings as [dow, dowLabel, dow3Letter]}
        {@const day = defaults[dow3Letter]}
        {@const intensity = day.availability ? (day.intensity ?? 'advisor') : 'advisor'}
        <!-- Into the page's dialog target, where the components are styled on every page (Profile Settings too) -->
        <Popover.Root portal="#shadcn-ui-dialog-target">
            <Popover.Trigger {disabled}
                aria-label="{dowLabel}: {day.availability ? compact(minutes(dow, dow3Letter)) : 'unavailable'}{intensity !== 'advisor' ? `, ${intensity} intensity` : ''}. Change"
                class={cn("flex flex-col items-center gap-0.5 rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring px-0.5 py-1.5 leading-tight transition-colors disabled:opacity-60",
                    day.availability ? "border-slate-300 bg-white hover:border-xerteal-400" : "border-dashed border-slate-300 bg-slate-50 hover:border-xerteal-300")}>
                <span class="text-xs font-semibold text-slate-600">{dow3Letter}</span>
                <span class={cn("text-xs sm:text-sm tabular-nums", day.availability ? "text-slate-800" : "text-slate-400")}>
                    {day.availability ? compact(minutes(dow, dow3Letter)) : "Off"}
                </span>
                <span class={cn("h-3 text-[10px] font-semibold uppercase tracking-wide",
                    intensity === 'high' ? "text-red-700" : intensity === 'low' ? "text-xerteal-700" : "text-transparent")}>
                    {intensity === 'high' ? "High" : intensity === 'low' ? "Low" : "·"}
                </span>
            </Popover.Trigger>
            <AvailabilityPopover bind:defaultAvailability={defaults[dow3Letter]} {dowLabel} id="{id}-{dow3Letter}" medians={getUsualAvailability(trainingMedians, dow)}/>
        </Popover.Root>
    {/each}
</div>
